// Plan-drawer data layer: read the governance-plane view-model for a scope, and governed writes for
// goals/milestones/todos/proposals. Every write carries a per-action Idempotency-Key (§220-B) and, at
// the call site, is gated on spine-authority. Fixture mode mutates the cache optimistically so the
// surface is fully exercisable without the (backend-required) /api/v1/plan/* routes existing yet.

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { USE_MSW } from '../../shared/config';
import { apiGet, apiWrite, newIdempotencyKey } from '../../shared/api/client';
import { ROUTES } from '../../shared/api/routes';
import type { Enveloped } from '../../shared/api/envelope';
import { fixturePlan } from '../../shared/fixtures/plan-seed';
import type { PlanScope, PlanGoal, PlanMilestone, PlanTodo, PlanIntent } from '../../shared/fixtures/plan-types';

type EntityKind = 'goal' | 'milestone' | 'todo';

const planKey = (scopeId: string) => ['plan', scopeId] as const;

export function usePlan(scopeId: string | null): UseQueryResult<Enveloped<PlanScope>> {
  return useQuery({
    enabled: !!scopeId,
    queryKey: planKey(scopeId ?? ''),
    queryFn: () => {
      if (USE_MSW) return Promise.resolve<Enveloped<PlanScope>>({ data: fixturePlan(scopeId!), data_class: 'starter' });
      return apiGet<PlanScope>(ROUTES.plan(scopeId!));
    },
  });
}

// A local id generator for optimistic fixture rows (crypto.randomUUID exists in jsdom + browsers).
function localId(prefix: string): string {
  return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
}

function patchScope(prev: Enveloped<PlanScope> | undefined, fn: (s: PlanScope) => PlanScope): Enveloped<PlanScope> | undefined {
  if (!prev) return prev;
  return { ...prev, data: fn(prev.data) };
}

export interface PlanWriteApi {
  addEntity: (kind: EntityKind, text: string) => Promise<void>;
  renameEntity: (kind: EntityKind, id: string, text: string) => Promise<void>;
  deleteEntity: (kind: EntityKind, id: string) => Promise<void>;
  promoteTodo: (todoId: string) => Promise<void>;
  decideProposal: (proposalId: string, verdict: 'approved' | 'rejected') => Promise<void>;
  isPending: boolean;
}

/** Governed plan writes for a scope. In fixture mode each optimistically edits the cache; live mode
 *  POSTs/PATCHes /api/v1/plan/* (create/promote route through /events + /sign-offs) with Idempotency-Key. */
export function usePlanWrite(scopeId: string): PlanWriteApi {
  const qc = useQueryClient();
  const key = planKey(scopeId);

  const write = useMutation({
    mutationFn: async (op: { run: (s: PlanScope) => PlanScope; live: () => Promise<unknown> }) => {
      qc.setQueryData<Enveloped<PlanScope>>(key, (prev) => patchScope(prev, op.run));
      if (!USE_MSW) await op.live();
    },
  });

  const listKey = (kind: EntityKind): 'goals' | 'milestones' | 'todos' =>
    kind === 'goal' ? 'goals' : kind === 'milestone' ? 'milestones' : 'todos';

  return {
    isPending: write.isPending,
    addEntity: (kind, text) =>
      write.mutateAsync({
        run: (s) => {
          const id = localId(kind[0]);
          if (kind === 'goal') return { ...s, goals: [...s.goals, { id, text, provenance: 'human' } as PlanGoal] };
          if (kind === 'milestone') return { ...s, milestones: [...s.milestones, { id, text, provenance: 'human' } as PlanMilestone] };
          return { ...s, todos: [...s.todos, { id, text, provenance: 'human', canPromote: true } as PlanTodo] };
        },
        live: () => apiWrite('POST', ROUTES.planEntity, { scope_id: scopeId, kind, text }, { idempotencyKey: newIdempotencyKey() }),
      }),
    renameEntity: (kind, id, text) =>
      write.mutateAsync({
        run: (s) => ({ ...s, [listKey(kind)]: (s[listKey(kind)] as { id: string }[]).map((e) => (e.id === id ? { ...e, text } : e)) }) as PlanScope,
        live: () => apiWrite('PATCH', ROUTES.planEntityById(id), { text }, { idempotencyKey: newIdempotencyKey() }),
      }),
    deleteEntity: (kind, id) =>
      write.mutateAsync({
        run: (s) => ({ ...s, [listKey(kind)]: (s[listKey(kind)] as { id: string }[]).filter((e) => e.id !== id) }) as PlanScope,
        live: () => apiWrite('DELETE', ROUTES.planEntityById(id), undefined, { idempotencyKey: newIdempotencyKey() }),
      }),
    // Promote = the todo becomes a tracked intent on the spine (routes through /events + sign-offs).
    promoteTodo: (todoId) =>
      write.mutateAsync({
        run: (s) => {
          const todo = s.todos.find((t) => t.id === todoId);
          if (!todo) return s;
          const intent: PlanIntent = { id: localId('i'), label: todo.text, lifecycle: 'active', settled: false };
          return { ...s, todos: s.todos.filter((t) => t.id !== todoId), intents: [...s.intents, intent] };
        },
        live: () => apiWrite('POST', ROUTES.events, { kind: 'intent', promote_todo_id: todoId, scope_id: scopeId }, { idempotencyKey: newIdempotencyKey() }),
      }),
    // Approve/dismiss an AI proposal — a spine decision (routes through /sign-offs).
    decideProposal: (proposalId, verdict) =>
      write.mutateAsync({
        run: (s) => ({ ...s, proposals: s.proposals.filter((p) => p.id !== proposalId) }),
        live: () => apiWrite('POST', ROUTES.signOffs, { proposal_id: proposalId, verdict }, { idempotencyKey: newIdempotencyKey() }),
      }),
  };
}
